import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Monitor, Smartphone, Globe } from 'lucide-react';

const Downloads = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="downloads-page">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/big-clipboard-text-logo.jpg" 
                alt="Big Clipboard" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                Home
              </Link>
              <Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                Features
              </Link>
              <Link to="/#faq" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                FAQ
              </Link>
              <Link to="/downloads" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                Downloads
              </Link>
            </nav>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <Link 
                to="/login" 
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                Sign in
              </Link>
              <Link 
                to="/register" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
              >
                Sign up
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white">
              <div className="px-4 py-4 space-y-4">
                <Link 
                  to="/" 
                  className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link 
                  to="/#features" 
                  className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Features
                </Link>
                <Link 
                  to="/#faq" 
                  className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Link 
                  to="/downloads" 
                  className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Downloads
                </Link>
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Link 
                    to="/login" 
                    className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link 
                    to="/register" 
                    className="block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200 text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16" />

      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Downloads</h1>
          <p className="text-lg text-gray-600 mb-8">
            Download the latest tools and apps to use Big Clipboard on your devices. Choose your platform below.
          </p>
          <div className="bg-white rounded-lg shadow p-8 space-y-10 border border-gray-200">
            {/* Browser Extensions Section - Emphasized */}
            <div className="relative bg-primary-50 border-2 border-primary-600 rounded-xl p-8 shadow-lg">
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">Recommended</span>
              <h2 className="text-2xl font-bold text-primary-700 mb-2">Browser Extensions</h2>
              <div className="mb-4">
                <div className="bg-primary-100 border-l-4 border-primary-600 text-primary-800 p-4 rounded mb-2 font-medium">
                  Install the free extension to your browser is all you need!
                </div>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center space-x-3">
                  <img src="/chrome-logo.png" alt="Chrome" className="w-5 h-5" />
                  <a href="https://chromewebstore.google.com/detail/big-clipboard/ebcbicnmiahafaebbllibmpgneocaaih" className="text-primary-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Chrome Extension</a>
                </li>
                <li className="flex items-center space-x-3">
                  <img src="/firefox-logo.png" alt="Firefox" className="w-5 h-5" />
                  <a href="https://addons.mozilla.org/en-US/firefox/addon/big-clipboard/" className="text-primary-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Firefox Extension</a>
                </li>
                <li className="flex items-center space-x-3">
                  <img src="/edge-logo.png" alt="Edge" className="w-5 h-5" />
                  <a href="https://microsoftedge.microsoft.com/addons/detail/big-clipboard/oaeaopkmnmjaalmeilcjcegilbdogpok" className="text-primary-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Edge Extension</a>
                </li>
                {/* <li className="flex items-center space-x-3">
                  <img src="/safari-logo.png" alt="Safari" className="w-5 h-5" />
                  <span className="text-gray-900 font-medium">Safari Extension</span> <span className="text-gray-500">- Coming Soon</span>
                </li> */}
              </ul>
            </div>
            {/* Mobile Apps Section */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Mobile Apps</h2>
              <p className="text-gray-600 mb-4">Mobile apps will let you sync your clipboard on the go. Perfect for copying and pasting between your phone and other devices. (Coming soon!)</p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-3">
                  <img src="/android-logo.png" alt="Android" className="w-5 h-5" />
                  <span className="text-gray-900 font-medium">Download for Android</span> <span className="text-gray-500">- Coming Soon</span>
                </li>
                <li className="flex items-center space-x-3">
                  <img src="/html-homepage/images/png_icons/mac-logo.png" alt="iPhone" className="w-5 h-5" />
                  <span className="text-gray-900 font-medium">Download for iPhone</span> <span className="text-gray-500">- Coming Soon</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Downloads; 